import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { CaseContactMethodViewModel, PolicyHolderInfo } from "interfaces/Mueller.Business.Models";
import { InitialState } from "reducers/initialState";
import { Button, SelectionControl, TextField } from "react-md";
import { contactTypeCodes } from "typeConstants/contactEfforts";

interface AppointmentConfirmationProps {
  onSave: (contacts: string[]) => void;
  onCancel: () => void;
  saveButtonLabel: string;
  // Redux
  policyHolderInfo: PolicyHolderInfo;
  contactMethods: CaseContactMethodViewModel[];
}

interface AppointmentConfirmationForm {
  sendConfirmation: boolean;
  sendToDesignated1: boolean;
  sendToDesignated2: boolean;
  sendToDesignatedEmail: boolean;
  sendToHomePhone: boolean;
  sendToWorkPhone: boolean;
  sendToEmail: boolean;
  sendToOther: boolean;
  otherContact: string;
}

const AppointmentConfirmation = (props: AppointmentConfirmationProps) => {
  const [form, setForm] = useState<AppointmentConfirmationForm>({
    sendConfirmation: true,
    sendToDesignated1: false,
    sendToDesignated2: false,
    sendToDesignatedEmail: false,
    sendToHomePhone: false,
    sendToWorkPhone: false,
    sendToEmail: false,
    sendToOther: false,
    otherContact: ""
  });

  const [designated1, setDesignated1] = useState<string>(null);
  const [designated2, setDesignated2] = useState<string>(null);
  const [designatedEmail, setDesignatedEmail] = useState<string>(null);

  useEffect(() => {
    if (props.contactMethods) {
      for(let contact of props.contactMethods) {
        if (contact.contactTypeCode === contactTypeCodes.D1) {
          setDesignated1(contact.contactValue);
        } else if (contact.contactTypeCode === contactTypeCodes.D2) {
          setDesignated2(contact.contactValue);
        } else if (contact.contactTypeCode === contactTypeCodes.DE) {
          setDesignatedEmail(contact.contactValue);
        }
      }
    }
  }, []);

  const onSendConfirmationChange = (value: boolean) => setForm({...form, sendConfirmation: value});
  const onSendToDesignated1Change = (value: boolean) => setForm({...form, sendToDesignated1: value});
  const onSendToDesignated2Change = (value: boolean) => setForm({...form, sendToDesignated2: value});
  const onSendToDesignatedEmailChange = (value: boolean) => setForm({...form, sendToDesignatedEmail: value});
  const onSendToHomePhoneChange = (value: boolean) => setForm({...form, sendToHomePhone: value});
  const onSendToWorkPhoneChange = (value: boolean) => setForm({...form, sendToWorkPhone: value});
  const onSendToEmailChange = (value: boolean) => setForm({...form, sendToEmail: value});
  const onSendToOtherChange = (value: boolean) => setForm({...form, sendToOther: value});
  const onOtherChange = (value: string) => setForm({...form, otherContact: value});

  const handleSave = () => {
    const contacts: string[] = [];
    if (form.sendConfirmation) {
      if (form.sendToDesignated1 && designated1) contacts.push(designated1);
      if (form.sendToDesignated2 && designated2) contacts.push(designated2);
      if (form.sendToDesignatedEmail && designatedEmail) contacts.push(designatedEmail);
      if (form.sendToHomePhone) contacts.push(props.policyHolderInfo?.policyholderPhoneHome);
      if (form.sendToWorkPhone) contacts.push(props.policyHolderInfo?.policyholderPhoneWork);
      if (form.sendToEmail) contacts.push(props.policyHolderInfo?.policyholderEmail);
      if (form.sendToOther) contacts.push(form.otherContact);
    }
    props.onSave(contacts);
  };

  // TODO: Validate phone/email in other field?
  const isValid = !form.sendConfirmation 
    || (form.sendConfirmation && (form.sendToHomePhone || form.sendToWorkPhone || form.sendToEmail
      || form.sendToDesignated1 || form.sendToDesignated2 || form.sendToDesignatedEmail
      || (form.sendToOther && form.otherContact.length > 3)));

  return (
    <div>
      <SelectionControl
        id="assign-appointment-dialog-send-confirmation"
        label="Send Appointment Confirmation"
        type="checkbox"
        onChange={onSendConfirmationChange}
        value={form.sendConfirmation}
        name="Send Confirmation"
        defaultChecked={form.sendConfirmation}
      />
      {form.sendConfirmation &&
        <div className="assign-appointment-contact-options-container">
          {designated1 &&
            <SelectionControl
              id="assign-appointment-send-designated1"
              label={designated1}
              type="checkbox"
              onChange={onSendToDesignated1Change}
              value={form.sendToDesignated1}
              name="Designated 1"
              defaultChecked={form.sendToDesignated1}
            />
          }
          {designated2 &&
            <SelectionControl
              id="assign-appointment-send-designated2"
              label={designated2}
              type="checkbox"
              onChange={onSendToDesignated2Change}
              value={form.sendToDesignated2}
              name="Designated 2"
              defaultChecked={form.sendToDesignated2}
            />
          }
          {designatedEmail &&
            <SelectionControl
              id="assign-appointment-send-designated-email"
              label={designatedEmail}
              type="checkbox"
              onChange={onSendToDesignatedEmailChange}
              value={form.sendToDesignatedEmail}
              name="Designated Email"
              defaultChecked={form.sendToDesignatedEmail}
            />
          }
          {props.policyHolderInfo?.policyholderPhoneHome &&
            <SelectionControl
              id="assign-appointment-send-home-phone"
              label={props.policyHolderInfo?.policyholderPhoneHome}
              type="checkbox"
              onChange={onSendToHomePhoneChange}
              value={form.sendToHomePhone}
              name="Home Phone"
              defaultChecked={form.sendToHomePhone}
            />
          }
          {props.policyHolderInfo?.policyholderPhoneWork &&
            <SelectionControl
              id="assign-appointment-send-work-phone"
              label={props.policyHolderInfo?.policyholderPhoneWork}
              type="checkbox"
              onChange={onSendToWorkPhoneChange}
              value={form.sendToWorkPhone}
              name="Work Phone"
              defaultChecked={form.sendToWorkPhone}
            />
          }
          {props.policyHolderInfo?.policyholderEmail &&
            <SelectionControl
              id="assign-appointment-send-email"
              label={props.policyHolderInfo?.policyholderEmail}
              type="checkbox"
              onChange={onSendToEmailChange}
              value={form.sendToEmail}
              name="Email"
              defaultChecked={form.sendToEmail}
            />
          }
          <SelectionControl
            id="assign-appointment-send-other"
            label="Send to other email or phone number"
            type="checkbox"
            onChange={onSendToOtherChange}
            value={form.sendToOther}
            name="Other"
            defaultChecked={form.sendToOther}
          />
          {form.sendToOther && 
            <TextField
              id="assign-appointment-send-other-field"
              maxLength={500}
              rows={1}
              value={form.otherContact}
              onChange={onOtherChange}
              label="Enter an email address or phone number:"
            />
          }
        </div>
      }
      {!form.sendConfirmation && <p className="assign-appointment-no-confirm-warning">No Appointment Confirmation Will Be Sent</p>}
      <div className="assign-appointment-button-container">
        <Button raised onClick={props.onCancel}>Cancel</Button>
        <Button 
          raised 
          primary 
          onClick={handleSave}
          disabled={!isValid}
        >{props.saveButtonLabel}</Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  policyHolderInfo: state.caseForms.caseHeaderV2?.caseHeaderDto?.policyInfo?.policyHolderInfo,
  contactMethods: state.caseForms.caseHeaderV2?.caseHeaderDto?.policyInfo?.caseContactMethods
});

export default connect(mapStateToProps, null)(AppointmentConfirmation);